import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InventoryIcon from "@mui/icons-material/Inventory";
import GroupsIcon from "@mui/icons-material/Groups";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import BadgeIcon from "@mui/icons-material/Badge";

import { useAuth } from "../../context/authContext";
import { useTechnicians } from "./hooks/useTechnicians";
import { useSnackbar } from "./hooks/useSnackbar";
import {
  getTechnicianFields,
  TECHNICIAN_STATUS,
} from "./constants";
import { parseError } from "./utils";
import { C, SCROLL } from "./theme";

import TechnicianSidebarItem from "./components/TechnicianSidebarItem";
import TechnicianDetailPanel from "./components/TechnicianDetailPanel";
import HRGlobalKPIs from "./components/HRGlobalKPIs";
import TechnicianRankingTable from "./components/TechnicianRankingTable";
import TechnicianInventoryModal from "./components/TechnicianInventoryModal";
import ModalFormUniversal from "../../components/ModalFormUniversal";
import ModalConfirmUniversal from "../../components/ModalConfirmUniversal";

const STATUS_FILTERS = [
  { value: "all", label: "Todos" },
  { value: TECHNICIAN_STATUS.ACTIVE, label: "Activos" },
  { value: TECHNICIAN_STATUS.ON_LEAVE, label: "Permiso" },
  { value: TECHNICIAN_STATUS.INACTIVE, label: "Inactivos" },
];

export default function Tecnicos() {
  const { role } = useAuth();

  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [moduleTab, setModuleTab] = useState(0);

  const [modalCreate, setModalCreate] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalInventory, setModalInventory] = useState(false);
  const [selectedForInventory, setSelectedForInventory] = useState(null);
  const [selected, setSelected] = useState(null);

  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const debounceRef = useRef(null);

  const {
    technicians,
    users,
    loading,
    createTechnician,
    updateTechnician,
    deleteTechnician,
  } = useTechnicians();

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    if (technicians.length && !selectedTechnician) {
      setSelectedTechnician(technicians[0]);
    }
    if (
      selectedTechnician &&
      !technicians.find((t) => t._id === selectedTechnician._id)
    ) {
      setSelectedTechnician(technicians[0] || null);
    }
  }, [technicians, selectedTechnician]);

  const filteredTechnicians = useMemo(() => {
    const term = searchQuery.toLowerCase();
    let result = [...technicians];

    if (term) {
      result = result.filter(
        (t) =>
          t.name?.toLowerCase().includes(term) ||
          t.phone?.toLowerCase().includes(term) ||
          t.assignedVehicle?.toLowerCase().includes(term) ||
          t.zone?.toLowerCase().includes(term) ||
          t.user?.name?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((t) => t.status === statusFilter);
    }

    return result.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })
    );
  }, [technicians, searchQuery, statusFilter]);

  const technicianFields = getTechnicianFields(users);
  const canEditExpediente = ["admin", "rh"].includes(role);

  const handleCreateSubmit = async (data) => {
    try {
      await createTechnician(data);
      setModalCreate(false);
      showSnackbar("Técnico creado correctamente", "success");
    } catch (err) {
      showSnackbar(`Error al crear técnico: ${parseError(err)}`, "error");
    }
  };

  const handleEditOpen = (technician) => {
    setSelected(technician);
    setModalEdit(true);
  };

  const handleEditSubmit = async (data) => {
    try {
      await updateTechnician(selected._id, data);
      setModalEdit(false);
      if (selectedTechnician?._id === selected._id) {
        setSelectedTechnician((prev) => ({ ...prev, ...data }));
      }
      showSnackbar("Técnico actualizado correctamente", "success");
    } catch (err) {
      showSnackbar(`Error al actualizar técnico: ${parseError(err)}`, "error");
    }
  };

  const handleDeleteOpen = (technician) => {
    setSelected(technician);
    setModalDelete(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTechnician(selected._id);
      setModalDelete(false);
      showSnackbar("Técnico eliminado correctamente", "success");
    } catch (err) {
      showSnackbar(`Error al eliminar técnico: ${parseError(err)}`, "error");
    }
  };

  const handleInventoryOpen = (technician = null) => {
    setSelectedForInventory(technician);
    setModalInventory(true);
  };

  if (loading && technicians.length === 0) {
    return (
      <Box
        sx={{
          width: "calc(100% + (var(--app-page-padding, 24px) * 2))",
          height: "calc(100vh - var(--app-nav-height, 56px))",
          m: "calc(var(--app-page-padding, 24px) * -1)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: C.primary }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "calc(100% + (var(--app-page-padding, 24px) * 2))",
        height: "calc(100vh - var(--app-nav-height, 56px))",
        minHeight: 0,
        minWidth: 0,
        m: "calc(var(--app-page-padding, 24px) * -1)",
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(148px, 34vw) minmax(0, 1fr)",
          md: "280px minmax(0, 1fr)",
        },
        gap: 1.5,
        p: 1.5,
        bgcolor: "#fff",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* ── SIDEBAR DIRECTORIO ── */}
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
            <BadgeIcon sx={{ color: C.primary, fontSize: 20 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.text }}>
              Recursos Humanos
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textSoft, ml: "auto" }}>
              {technicians.length} técnicos
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, mt: 1 }}>
            <Box
              sx={{
                flex: 1,
                height: 36,
                display: "flex",
                alignItems: "center",
                px: 1.25,
                border: `1px solid ${C.border}`,
                borderRadius: 1.5,
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: C.textSoft }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar técnico..."
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  marginLeft: 8,
                  fontSize: 14,
                  background: "transparent",
                }}
              />
              {search && (
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 16, color: C.textSoft }} />
                </IconButton>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={() => setModalCreate(true)}
              sx={{
                minWidth: 42,
                width: 42,
                p: 0,
                bgcolor: C.primary,
                "&:hover": { bgcolor: C.primary },
              }}
            >
              <AddIcon />
            </Button>
          </Box>

          <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {STATUS_FILTERS.map((f) => (
              <Chip
                key={f.value}
                label={f.label}
                size="small"
                onClick={() => setStatusFilter(f.value)}
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: 10,
                  cursor: "pointer",
                  bgcolor: statusFilter === f.value ? C.primarySoft : "#f3f4f6",
                  color: statusFilter === f.value ? C.primary : C.textSoft,
                  border:
                    statusFilter === f.value
                      ? `1px solid ${C.primary}`
                      : `1px solid transparent`,
                }}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            ...SCROLL,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {filteredTechnicians.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center", color: C.textSoft }}>
              <Typography sx={{ fontSize: 13 }}>
                {search || statusFilter !== "all"
                  ? "Sin resultados"
                  : "No hay técnicos registrados"}
              </Typography>
            </Box>
          ) : (
            filteredTechnicians.map((tech) => (
              <TechnicianSidebarItem
                key={tech._id}
                technician={tech}
                selected={selectedTechnician?._id === tech._id}
                onClick={() => {
                  setSelectedTechnician(tech);
                  setModuleTab(0);
                }}
              />
            ))
          )}
        </Box>

        <Box sx={{ p: 1, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<InventoryIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleInventoryOpen(null)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              py: 0.75,
              borderColor: C.border,
              color: C.text,
              "&:hover": { borderColor: C.primary, bgcolor: C.primarySoft },
            }}
          >
            Inventario general
          </Button>
        </Box>
      </Paper>

      {/* ── PANEL PRINCIPAL ── */}
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Tabs del módulo */}
        <Box
          sx={{
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
            px: 1.5,
            bgcolor: "#fafafa",
          }}
        >
          <Tabs
            value={moduleTab}
            onChange={(_, v) => setModuleTab(v)}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                minHeight: 40,
                py: 0.5,
              },
            }}
          >
            <Tab
              icon={<GroupsIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Directorio"
            />
            <Tab
              icon={<LeaderboardIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Ranking & Desempeño"
            />
          </Tabs>
        </Box>

        {moduleTab === 1 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              p: 1.5,
              bgcolor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <HRGlobalKPIs technicians={technicians} loading={loading} />
            <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
              <TechnicianRankingTable />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <TechnicianDetailPanel
              technician={selectedTechnician}
              onEdit={handleEditOpen}
              onDelete={handleDeleteOpen}
              onViewInventory={handleInventoryOpen}
              canEditExpediente={canEditExpediente}
            />
          </Box>
        )}
      </Paper>

      {/* Modales */}
      <ModalFormUniversal
        open={modalCreate}
        onClose={() => setModalCreate(false)}
        title="Nuevo Técnico"
        fields={technicianFields}
        initialData={{}}
        onSubmit={handleCreateSubmit}
      />

      <ModalFormUniversal
        open={modalEdit}
        onClose={() => setModalEdit(false)}
        title={`Editar técnico: ${selected?.name || ""}`}
        fields={technicianFields}
        initialData={
          selected
            ? {
                name: selected.name,
                phone: selected.phone,
                assignedVehicle: selected.assignedVehicle,
                zone: selected.zone || "",
                status: selected.status,
              }
            : {}
        }
        onSubmit={handleEditSubmit}
      />

      <ModalConfirmUniversal
        open={modalDelete}
        onClose={() => setModalDelete(false)}
        title="Eliminar técnico"
        message={`¿Seguro que deseas eliminar a ${selected?.name}?`}
        confirmText="Eliminar"
        color="error"
        onConfirm={handleDeleteConfirm}
      />

      <TechnicianInventoryModal
        open={modalInventory}
        onClose={() => {
          setModalInventory(false);
          setSelectedForInventory(null);
        }}
        technician={selectedForInventory}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
